// src/components/layout/ChatColumn/actions/ChatRewind.jsx
// ==============================================
// ChatRewind.jsx
// Handles rewinding conversation to specific message
// ==============================================
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { HousePortal } from '@/App';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from '@/managers/ThemeProvider';
import { useConversation } from '@/managers/ConvoManager';

/* ========================
 * ChatRewindModal - Modal component for rewind confirmation
 * ======================== */
const ChatRewindModal = ({ onConfirm, onCancel, isRewinding, error }) => {
    const { isLightTheme } = useTheme();
    return (
        <HousePortal>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-glass">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className={`
                        ${isLightTheme 
                            ? 'bg-surface text-on-surface border border-border-color'
                            : 'bg-surface bg-opacity-80 border border-white/5'
                        } 
                        backdrop-blur-glass rounded-glass p-6 shadow-glass-md max-w-sm w-full
                    `}
                >
                    <h2 className={`
                        text-lg font-semibold mb-4 
                        ${isLightTheme ? 'text-on-surface' : 'text-white'}
                    `}>
                        Confirm Rewind
                    </h2>
                    <p className={`
                        mb-6 
                        ${isLightTheme ? 'text-on-surface/70' : 'text-white/70'}
                    `}>
                        This will move the selected message back to the input box and remove all messages after it. Are you sure?
                    </p>
                    {error && (
                        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-md text-error text-sm">
                            {error}
                        </div>
                    )}
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={onCancel}
                            disabled={isRewinding}
                            className={`
                                px-4 py-2 rounded-md hover:bg-surface transition-colors
                                ${isLightTheme 
                                    ? 'bg-surface-dark text-on-surface'
                                    : 'bg-surface-dark text-white/70'}
                                ${isRewinding ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isRewinding}
                            className={`
                                px-4 py-2 bg-warning text-white rounded-md 
                                hover:bg-warning-dark transition-colors
                                flex items-center gap-2
                                ${isRewinding ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                        >
                            {isRewinding ? (
                                <>
                                    <span className="animate-spin h-4 w-4 border-2 border-white/20 border-t-white rounded-full"></span>
                                    Rewinding...
                                </>
                            ) : (
                                'Rewind'
                            )}
                        </button>
                    </div>
                </motion.div>
            </div>
        </HousePortal>
    );
};

/* ========================
 * ChatRewind - Main component for chat rewind functionality
 * Manages the state and logic for rewinding.
 * ======================== */
const ChatRewind = ({ show, onClose, messageId, onError }) => {
    const { rewindToMessage } = useConversation();
    const [isRewinding, setIsRewinding] = useState(false);
    const [error, setError] = useState(null);

    const performRewind = async () => {
        setIsRewinding(true);
        setError(null);
        
        try {
            console.group('🔄 [ChatRewind] Starting rewind process');
            console.log('🎯 Rewinding to message:', messageId);
            
            const result = await rewindToMessage(messageId);
            
            if (result) {
                console.log('✅ Rewind completed successfully');
                onClose();
            } else {
                throw new Error('Rewind operation failed. Check console for details.');
            }
        } catch (err) {
            console.error('❌ Rewind failed:', err);
            const errorMessage = err.message || 'Failed to rewind conversation';
            setError(errorMessage);
            onError?.(errorMessage); // Propagate error for toast notifications
        } finally {
            setIsRewinding(false);
            console.groupEnd();
        }
    };

    return (
        <AnimatePresence>
            {show && (
                <ChatRewindModal
                    onConfirm={performRewind}
                    onCancel={onClose}
                    isRewinding={isRewinding}
                    error={error}
                />
            )}
        </AnimatePresence>
    );
};

ChatRewindModal.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isRewinding: PropTypes.bool,
    error: PropTypes.string
};

ChatRewind.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    messageId: PropTypes.string.isRequired,
    onError: PropTypes.func
};

export default ChatRewind;